/**
 * UnifiedReferencePicker — reference chip picker for the wizard's
 * `reference_picker` field. Supports six sources: excerpts, library, corpus
 * (docs+folders+buckets), notes, and podcast episodes.
 *
 * Selected items are stored as `ReferenceChip[]` with a per-chip `usage`
 * selector (cite / consult / quote_verbatim) that the Writing Agent honors.
 * Excerpts intentionally lead the source order per the intake spec.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { listNotes } from "@/lib/composerNotes";
import { listDocuments, listFolders, BUCKET_LABELS, BUCKET_ORDER } from "@/lib/workspaceCorpus";
import { fetchEpisodes } from "@/lib/podcasts";
import type { ReferenceChip, ReferenceSource } from "@/lib/composerTemplates";

const SOURCE_LABEL: Record<ReferenceSource, string> = {
  excerpts: "Excerpts",
  library: "Library",
  corpus: "Corpus",
  notes: "Notes",
  podcasts: "Podcasts",
};

const USAGE_LABEL: Record<NonNullable<ReferenceChip["usage"]>, string> = {
  cite: "Cite",
  consult: "Consult",
  quote_verbatim: "Quote verbatim",
};

type PickerItem = {
  kind: ReferenceSource;
  id: string;
  label: string;
  sublabel?: string;
};

type Props = {
  workspaceId: string | null;
  sources: ReferenceSource[];
  multi?: boolean;
  value: ReferenceChip[];
  onChange: (next: ReferenceChip[]) => void;
  disabled?: boolean;
};

export const UnifiedReferencePicker = ({
  workspaceId,
  sources,
  multi = true,
  value,
  onChange,
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false);

  const removeAt = (i: number) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  const setUsage = (i: number, usage: ReferenceChip["usage"]) => {
    const next = value.slice();
    next[i] = { ...next[i], usage };
    onChange(next);
  };

  const addChip = (chip: ReferenceChip) => {
    if (value.some((c) => c.kind === chip.kind && c.id === chip.id)) {
      toast.info("Already added");
      return;
    }
    const next = multi ? [...value, chip] : [chip];
    onChange(next);
    if (!multi) setOpen(false);
  };

  const canOpen = !disabled && !!workspaceId && sources.length > 0;

  return (
    <div className="space-y-2">
      {value.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {value.map((chip, i) => (
            <div
              key={`${chip.kind}:${chip.id}:${i}`}
              className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1.5"
            >
              <Badge variant="secondary" className="shrink-0 text-[10px] uppercase">
                {SOURCE_LABEL[chip.kind]}
              </Badge>
              <span className="min-w-0 flex-1 truncate text-sm">{chip.label}</span>
              <Select
                value={chip.usage ?? "consult"}
                onValueChange={(v) => setUsage(i, v as ReferenceChip["usage"])}
              >
                <SelectTrigger className="h-7 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(USAGE_LABEL) as Array<keyof typeof USAGE_LABEL>).map((u) => (
                    <SelectItem key={u} value={u} className="text-xs">
                      {USAGE_LABEL[u]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => removeAt(i)}
                aria-label="Remove reference"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      ) : null}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={!canOpen || (!multi && value.length > 0)}
      >
        <Plus className="mr-1 h-3.5 w-3.5" />
        {value.length === 0 ? "Add reference" : "Add another"}
      </Button>

      <PickerDialog
        open={open}
        onOpenChange={setOpen}
        workspaceId={workspaceId}
        sources={sources}
        onPick={addChip}
      />
    </div>
  );
};

// ---------- Dialog with tabbed source browser ----------

const PickerDialog = ({
  open,
  onOpenChange,
  workspaceId,
  sources,
  onPick,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  workspaceId: string | null;
  sources: ReferenceSource[];
  onPick: (chip: ReferenceChip) => void;
}) => {
  const [tab, setTab] = useState<ReferenceSource>(sources[0]);
  useEffect(() => {
    if (open) setTab(sources[0]);
  }, [open, sources]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add reference</DialogTitle>
        </DialogHeader>
        {!workspaceId ? (
          <p className="text-sm text-muted-foreground">No active workspace.</p>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as ReferenceSource)}>
            <TabsList className="flex w-full flex-wrap">
              {sources.map((s) => (
                <TabsTrigger key={s} value={s} className="text-xs">
                  {SOURCE_LABEL[s]}
                </TabsTrigger>
              ))}
            </TabsList>
            {sources.map((s) => (
              <TabsContent key={s} value={s} className="mt-3">
                <SourcePanel
                  active={tab === s}
                  source={s}
                  workspaceId={workspaceId}
                  onPick={(item) =>
                    onPick({
                      kind: item.kind,
                      id: item.id,
                      label: item.label,
                      usage: "consult",
                    })
                  }
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------- Per-source loader + list ----------

const SourcePanel = ({
  active,
  source,
  workspaceId,
  onPick,
}: {
  active: boolean;
  source: ReferenceSource;
  workspaceId: string;
  onPick: (item: PickerItem) => void;
}) => {
  const [items, setItems] = useState<PickerItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!active || items !== null) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const loaded = await loadSource(source, workspaceId);
        if (!cancelled) setItems(loaded);
      } catch (e) {
        if (!cancelled) {
          toast.error(`Could not load ${SOURCE_LABEL[source]}: ${(e as Error).message}`);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, source, workspaceId, items]);

  const filtered = useMemo(() => {
    const list = items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list.slice(0, 200);
    return list
      .filter(
        (it) =>
          it.label.toLowerCase().includes(needle) ||
          (it.sublabel ?? "").toLowerCase().includes(needle),
      )
      .slice(0, 200);
  }, [items, q]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${SOURCE_LABEL[source]}…`}
          className="pl-7 h-8 text-sm"
        />
      </div>
      <div className="max-h-[45vh] overflow-y-auto rounded-md border">
        {loading ? (
          <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-3 text-xs text-muted-foreground">
            {items && items.length === 0
              ? `Nothing in ${SOURCE_LABEL[source]} yet.`
              : "No matches."}
          </p>
        ) : (
          <ul className="divide-y">
            {filtered.map((it) => (
              <li key={`${it.kind}:${it.id}`}>
                <button
                  type="button"
                  onClick={() => onPick(it)}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-accent"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{it.label}</div>
                    {it.sublabel ? (
                      <div className="truncate text-xs text-muted-foreground">
                        {it.sublabel}
                      </div>
                    ) : null}
                  </div>
                  <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------- Source loaders ----------

const truncate = (s: string, n = 140) => {
  const clean = s.replace(/\s+/g, " ").trim();
  return clean.length > n ? `${clean.slice(0, n - 1)}…` : clean;
};

type ExcerptRow = {
  id: string;
  body_text: string | null;
  library_items:
    | { display_title: string | null; title: string | null; author_text: string | null }
    | null;
};

type LibraryRow = {
  id: string;
  display_title: string | null;
  title: string | null;
  author_text: string | null;
  container_title: string | null;
};

const loadSource = async (
  source: ReferenceSource,
  workspaceId: string,
): Promise<PickerItem[]> => {
  switch (source) {
    case "excerpts": {
      const { data, error } = await supabase
        .from("excerpts")
        .select("id, body_text, library_items(display_title, title, author_text)")
        .eq("workspace_id", workspaceId)
        .order("updated_at", { ascending: false })
        .limit(300);
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as ExcerptRow[]).map((r) => {
        const lib = r.library_items;
        const src =
          lib?.display_title || lib?.title
            ? `${lib.display_title ?? lib.title}${lib.author_text ? ` — ${lib.author_text}` : ""}`
            : undefined;
        return {
          kind: "excerpts" as const,
          id: r.id,
          label: truncate(r.body_text ?? "(empty excerpt)", 120),
          sublabel: src,
        };
      });
    }
    case "library": {
      const { data, error } = await supabase
        .from("library_items")
        .select("id, display_title, title, author_text, container_title")
        .eq("workspace_id", workspaceId)
        .order("updated_at", { ascending: false })
        .limit(300);
      if (error) throw new Error(error.message);
      return ((data ?? []) as unknown as LibraryRow[]).map((r) => ({
        kind: "library" as const,
        id: r.id,
        label: r.display_title || r.title || "(untitled)",
        sublabel:
          [r.author_text, r.container_title].filter(Boolean).join(" · ") || undefined,
      }));
    }

    case "corpus": {
      const [docs, folders] = await Promise.all([
        listDocuments(workspaceId),
        listFolders(workspaceId),
      ]);
      const items: PickerItem[] = [];
      for (const b of BUCKET_ORDER) {
        items.push({ kind: "corpus", id: `bucket:${b}`, label: BUCKET_LABELS[b], sublabel: "Bucket" });
      }
      for (const f of folders) {
        items.push({ kind: "corpus", id: `folder:${f.id}`, label: f.name, sublabel: "Folder" });
      }
      for (const d of docs) {
        items.push({ kind: "corpus", id: `doc:${d.id}`, label: d.title, sublabel: "Document" });
      }
      return items;
    }
    case "notes": {
      const notes = await listNotes(workspaceId);
      return notes.map((n) => ({
        kind: "notes" as const,
        id: n.id,
        label: n.title?.trim() || truncate(n.body ?? "(empty note)", 80),
        sublabel: n.title ? truncate(n.body ?? "", 80) : undefined,
      }));
    }
    case "podcasts": {
      const eps = await fetchEpisodes({ workspaceIds: [workspaceId], filter: "all" });
      return eps.map((e) => ({
        kind: "podcasts" as const,
        id: e.id,
        label: e.title || "(untitled episode)",
        sublabel: e.published_at ? new Date(e.published_at).toLocaleDateString() : undefined,
      }));
    }
  }
};

export default UnifiedReferencePicker;
